import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CalendarDays,
  FileCheck,
  FolderOpen,
  Layers,
  MapPin,
  MessageCircle,
  Pin,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ActivitySparkline } from "@/components/dashboard/ActivitySparkline";
import { ClimateProjectsStrip } from "@/components/dashboard/ClimateProjectsStrip";
import { EventMemoriesCard } from "@/components/dashboard/EventMemoriesCard";
import { EventsSpotlight } from "@/components/dashboard/EventsSpotlight";
import { FileTypeDonut } from "@/components/dashboard/FileTypeDonut";
import { WorkflowBars } from "@/components/dashboard/WorkflowBars";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useDocumentAnalytics, useDocumentCounts, useDocuments } from "@/hooks/useDocuments";
import { useEvents } from "@/hooks/useEvents";
import { useThreads } from "@/hooks/useForum";
import { usePendingReportCount } from "@/hooks/useReports";
import { FILE_TYPE_STYLES } from "@/lib/file-icons";
import { formatDate, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { WorkflowState } from "@/lib/types";

export default function Dashboard() {
  const { data: user } = useCurrentUser();
  const counts        = useDocumentCounts();
  const analytics     = useDocumentAnalytics();
  const recent        = useDocuments({ ordering: "-updated_at" });
  // Director+ only — the hook short-circuits on `enabled: false` for users.
  const pendingReports = usePendingReportCount({ enabled: !!user?.is_director_level });
  const recentThreads  = useThreads({});
  // Spotlight shows every event the actor can see (published + own
  // drafts for Director+), newest first. Filtering by status=upcoming
  // earlier hid anything draft or past-dated, which made the carousel
  // empty for most users. Backend already gates visibility by role.
  const spotlightEvents = useEvents({ ordering: "-start_at" });
  // The dedicated list card below still focuses on the calendar
  // (upcoming only, oldest-first so the next thing surfaces).
  const upcomingEvents  = useEvents({ status: "upcoming", ordering: "start_at" });

  if (!user) return null;

  return (
    <div className="mx-auto max-w-7xl space-y-8">
      {/* ---------- Greeting ---------- */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            {new Date().toLocaleDateString(undefined, {
              weekday: "long", month: "long", day: "numeric",
            })}
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">
            Welcome back, {user.first_name || user.full_name}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {greetingSub(user.role)}
          </p>
        </div>
        <Button asChild className="self-start sm:self-auto">
          <Link to="/library">
            Open library
            <ArrowUpRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </header>

      {/* ---------- Events spotlight + memories ----------
          Two-column band: the big hero takes the left, a slim memories
          slideshow sits in a side column. Stacks on narrow screens. */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <EventsSpotlight
          events={spotlightEvents.data?.results ?? []}
          loading={spotlightEvents.isLoading}
        />
        {/* Auto-hides itself when there are no memories anywhere yet,
            so the grid silently collapses to one column when empty. */}
        <EventMemoriesCard />
      </div>

      {/* ---------- Stat cards ---------- */}
      {/* Card count varies by role (2–5). 5-col on xl keeps everything
          on one line for admins; smaller breakpoints wrap cleanly. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <StatCard
          label="Documents you can see"
          value={counts.data?.total}
          icon={FolderOpen}
          loading={counts.isLoading}
          accent="slate"
          href="/library"
        />
        <StatCard
          label="Published"
          value={counts.data?.published}
          icon={Sparkles}
          loading={counts.isLoading}
          accent="emerald"
          href="/library?workflow_state=published"
        />
        {user.is_director_level && (
          <StatCard
            label="Pending director review"
            value={counts.data?.pending_review}
            icon={FileCheck}
            loading={counts.isLoading}
            accent="amber"
            href="/verification"
          />
        )}
        {user.is_ceo_level && (
          <StatCard
            label="Pending CEO sign-off"
            value={counts.data?.pending_ceo_review}
            icon={ShieldCheck}
            loading={counts.isLoading}
            accent="violet"
            href="/verification"
          />
        )}
        {user.is_director_level && (
          <StatCard
            label="Flagged forum posts"
            value={pendingReports.data?.pending}
            icon={ShieldAlert}
            loading={pendingReports.isLoading}
            accent="rose"
            href="/moderation"
          />
        )}
      </div>

      {/* ---------- Active climate projects strip ----------
          Horizontal scroll band of "what's happening now" tiles.
          Auto-hides when no active project exists. */}
      <ClimateProjectsStrip />

      {/* ---------- Analytics row ----------
          Three cards: 30-day uploads sparkline (wide), file-type donut,
          workflow-state stacked bar. All driven by /documents/analytics/
          and scoped to what the actor can see. */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="border-border/60 shadow-soft lg:col-span-1">
          <CardHeader className="space-y-1 pb-3">
            <CardTitle className="text-base">Upload activity</CardTitle>
            <CardDescription>Documents added per day, last 30 days.</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <ActivitySparkline
              data={analytics.data?.activity_30d}
              loading={analytics.isLoading}
            />
          </CardContent>
        </Card>

        <Card className="border-border/60 shadow-soft lg:col-span-1">
          <CardHeader className="space-y-1 pb-3">
            <CardTitle className="text-base">By type</CardTitle>
            <CardDescription>What's in the library right now.</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <FileTypeDonut
              data={analytics.data?.by_file_type}
              loading={analytics.isLoading}
            />
          </CardContent>
        </Card>

        <Card className="border-border/60 shadow-soft lg:col-span-1">
          <CardHeader className="space-y-1 pb-3">
            <CardTitle className="text-base">Workflow status</CardTitle>
            <CardDescription>Where things sit in the approval chain.</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <WorkflowBars
              data={analytics.data?.by_workflow_state}
              loading={analytics.isLoading}
            />
          </CardContent>
        </Card>
      </div>

      {/* ---------- Recent activity ---------- */}
      <Card className="border-border/60 shadow-soft">
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 border-b border-border/60 bg-muted/30 py-4">
          <div>
            <CardTitle className="text-base">Recently updated</CardTitle>
            <CardDescription>The last documents you can access.</CardDescription>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/library">
              View all
              <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          </Button>
        </CardHeader>

        <CardContent className="p-0">
          {recent.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : recent.data && recent.data.results.length > 0 ? (
            <ul className="divide-y divide-border/60">
              {recent.data.results.slice(0, 6).map((doc) => {
                const style = FILE_TYPE_STYLES[doc.file_type];
                const Icon  = style.icon;
                return (
                  <li
                    key={doc.id}
                    className="flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-muted/30"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                        style.chipClass,
                      )}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-medium">
                            {doc.title}
                          </span>
                          {doc.file_count > 1 && (
                            <span
                              className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-primary/30 bg-primary/10 px-1.5 py-px text-[10px] font-semibold text-primary"
                              title={`Bundle of ${doc.file_count} files`}
                            >
                              <Layers className="h-3 w-3" />
                              {doc.file_count}
                            </span>
                          )}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {doc.file_type_display}
                          {doc.owner_name && <> · {doc.owner_name}</>}
                          {" · "}
                          {formatDateTime(doc.updated_at)}
                        </div>
                      </div>
                    </div>
                    <WorkflowBadge state={doc.workflow_state} label={doc.workflow_state_display} />
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="px-6 py-16 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <FolderOpen className="h-5 w-5 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-semibold">No documents yet</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Upload your first one from the library.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---------- Forum + Events ---------- */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ForumActivityCard
          loading={recentThreads.isLoading}
          threads={recentThreads.data?.results ?? []}
        />
        <UpcomingEventsCard
          loading={upcomingEvents.isLoading}
          events={upcomingEvents.data?.results ?? []}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Forum activity — five most recently updated threads
// ---------------------------------------------------------------------------

interface ForumActivityCardProps {
  loading: boolean;
  threads: { id: string; title: string; is_pinned: boolean; post_count: number;
             last_post_at: string; category_display: string }[];
}

function ForumActivityCard({ loading, threads }: ForumActivityCardProps) {
  const shown = threads.slice(0, 5);
  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 border-b border-border/60 bg-muted/30 py-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
            Forum activity
          </CardTitle>
          <CardDescription>Where the conversation is moving.</CardDescription>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/forum">View all<ArrowUpRight className="ml-1.5 h-3.5 w-3.5" /></Link>
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : shown.length > 0 ? (
          <ul className="divide-y divide-border/60">
            {shown.map((t) => (
              <li key={t.id}>
                <Link
                  to={`/forum/threads/${t.id}`}
                  className="flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-muted/30"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {t.is_pinned && <Pin className="h-3 w-3 shrink-0 fill-amber-400 text-amber-500" />}
                      <span className="truncate text-sm font-medium">{t.title}</span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {t.category_display} · {t.post_count} {t.post_count === 1 ? "post" : "posts"} · {formatDateTime(t.last_post_at)}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyPanel icon={MessageCircle} title="No threads yet" hint="Start one from the Forum." />
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Upcoming events — next five
// ---------------------------------------------------------------------------

interface UpcomingEventsCardProps {
  loading: boolean;
  events: { id: string; title: string; start_at: string; location: string;
            event_type_display: string }[];
}

function UpcomingEventsCard({ loading, events }: UpcomingEventsCardProps) {
  const shown = events.slice(0, 5);
  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 border-b border-border/60 bg-muted/30 py-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            Upcoming events
          </CardTitle>
          <CardDescription>What's coming up on the calendar.</CardDescription>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/events">View all<ArrowUpRight className="ml-1.5 h-3.5 w-3.5" /></Link>
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : shown.length > 0 ? (
          <ul className="divide-y divide-border/60">
            {shown.map((e) => (
              <li key={e.id}>
                <Link
                  to={`/events/${e.id}`}
                  className="flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-muted/30"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{e.title}</div>
                    <div className="mt-0.5 flex items-center gap-3 truncate text-xs text-muted-foreground">
                      <span>{formatDate(e.start_at)}</span>
                      {e.location && (
                        <span className="flex items-center gap-1 truncate">
                          <MapPin className="h-3 w-3" />{e.location}
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline" className="shrink-0 font-medium">
                    {e.event_type_display}
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyPanel icon={CalendarDays} title="Nothing scheduled" hint="Director+ can publish events." />
        )}
      </CardContent>
    </Card>
  );
}

function EmptyPanel({
  icon: Icon, title, hint,
}: { icon: LucideIcon; title: string; hint: string }) {
  return (
    <div className="px-6 py-12 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <h3 className="text-sm font-semibold">{title}</h3>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function greetingSub(role: string): string {
  switch (role) {
    case "admin":    return "You're in the system top tier — full visibility.";
    case "ceo":      return "Items waiting for your final sign-off appear below.";
    case "director": return "Your department's submissions land in your verification queue.";
    default:         return "Your shared and published resources are below.";
  }
}

/** Accent → gradient + icon tint combo used on the stat cards.
 *  Each entry carries a `dark:` variant so the wash stays visible on dark
 *  backgrounds — the original `to-white` washed the card out, hiding the
 *  label and the number. */
const ACCENT: Record<string, { ring: string; icon: string; bar: string }> = {
  slate: {
    ring: "from-slate-100 to-white dark:from-slate-500/10 dark:to-transparent",
    icon: "bg-muted text-foreground/80 dark:bg-slate-500/20 dark:text-slate-200",
    bar:  "bg-slate-300 dark:bg-slate-500/60",
  },
  emerald: {
    ring: "from-emerald-50 to-white dark:from-emerald-500/15 dark:to-transparent",
    icon: "bg-emerald-100 text-emerald-700 dark:text-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-300",
    bar:  "bg-emerald-400 dark:bg-emerald-500/70",
  },
  amber: {
    ring: "from-amber-50 to-white dark:from-amber-500/15 dark:to-transparent",
    icon: "bg-amber-100 text-amber-700 dark:text-amber-300 dark:bg-amber-500/20 dark:text-amber-300",
    bar:  "bg-amber-400 dark:bg-amber-500/70",
  },
  violet: {
    ring: "from-violet-50 to-white dark:from-violet-500/15 dark:to-transparent",
    icon: "bg-violet-100 text-violet-700 dark:text-violet-300 dark:bg-violet-500/20 dark:text-violet-300",
    bar:  "bg-violet-400 dark:bg-violet-500/70",
  },
  rose: {
    ring: "from-rose-50 to-white dark:from-rose-500/15 dark:to-transparent",
    icon: "bg-rose-100 text-rose-700 dark:text-rose-300 dark:bg-rose-500/20 dark:text-rose-300",
    bar:  "bg-rose-400 dark:bg-rose-500/70",
  },
};

interface StatCardProps {
  label: string;
  value?: number;
  icon: LucideIcon;
  loading?: boolean;
  accent: keyof typeof ACCENT;
  href?: string;
}

function StatCard({ label, value, icon: Icon, loading, accent, href }: StatCardProps) {
  const a = ACCENT[accent];
  const card = (
    <Card
      className={cn(
        "relative overflow-hidden border-border/60 shadow-soft transition-all",
        "hover:shadow-soft-lg hover:-translate-y-0.5",
        href && "cursor-pointer",
      )}
    >
      {/* Soft gradient wash — sets the accent without screaming. */}
      <div className={cn("pointer-events-none absolute inset-0 bg-gradient-to-br", a.ring)} />
      {/* Thin coloured rail along the top. */}
      <div className={cn("absolute inset-x-0 top-0 h-0.5", a.bar)} />

      <CardContent className="relative p-5">
        <div className="flex items-start justify-between">
          <span className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
            {label}
          </span>
          <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg", a.icon)}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <div className="mt-4">
          {loading
            ? <Skeleton className="h-9 w-20" />
            : <div className="font-mono text-3xl font-bold tabular-nums tracking-tight">
                {(value ?? 0).toLocaleString()}
              </div>}
        </div>
      </CardContent>
    </Card>
  );
  return href ? <Link to={href}>{card}</Link> : card;
}

function WorkflowBadge({ state, label }: { state: WorkflowState; label: string }) {
  // Tone keyed to the workflow stage so it scans without reading.
  // Each pill has light + dark variants for bg/text/border so it stays
  // legible on either theme — the previous `text-<hue>-700` was unreadable
  // against the dark wash.
  const className =
    state === "published"          ? "bg-emerald-50 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30"
  : state === "pending_review"     ? "bg-amber-50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30"
  : state === "pending_ceo_review" ? "bg-violet-50 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-500/30 dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-500/30"
  : state === "rejected"           ? "bg-rose-50 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-500/30 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30"
  : state === "private_only"       ? "bg-blue-50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30"
                                   : "bg-muted/40 text-foreground/80 border-border";
  return (
    <Badge variant="outline" className={cn("border font-medium", className)}>
      {label}
    </Badge>
  );
}
